import ListTypeDetailActions from '@/components/feature/list-type/list-type-detail-actions';
import BackIcon from '@/components/icons/BackIcon';
import type { ListTypeListItem } from '@/types/list-type';
import { formatKoreanDateTTS, formatListTypeDateRange } from '@/utils/date-time';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import { useConfigStore } from '@/stores/configStore';
import clsx from 'clsx';
import { CalendarIcon } from '@/components/icons/calendarIcon';
import { useCallback, useRef, useState } from 'react';
import { ChevronUpIcon } from '@/components/icons';
import { getS3UploadUrl, isS3UploadUrl } from '@/utils/s3Config';
import { useAccessibilityTTS } from '@/hooks';

interface ListTypeDetailHorizontalProps {
    detail: ListTypeListItem;
    onBack: () => void;
    onQrClick: () => void;
    onDirectionClick: (detail: ListTypeListItem) => void;
}

const ListTypeDetailHorizontal = ({ detail, onBack, onQrClick, onDirectionClick }: ListTypeDetailHorizontalProps) => {
    const { t } = useTranslation();
    const { speak } = useAccessibilityTTS();

    const screenMode = useConfigStore((s) => s.config?.ui?.screenMode);
    const contrast = useConfigStore((state) => state.config.ui.contrast);

    const title = detail.title || '';
    const body = detail.contents || '';
    const dateRange = formatListTypeDateRange(detail.eventDateBegin, detail.eventDateEnd, t);
    const dateRangeTTS = formatKoreanDateTTS(detail.eventDateBegin, detail.eventDateEnd, t);

    const url = detail?.thumbnailUrl?.url;
    const urlPreview: string | null | undefined = url ? (isS3UploadUrl(url) ? url : getS3UploadUrl(url)) : '';

    const scrollRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [showScrollTop, setShowScrollTop] = useState(false);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        setShowScrollTop(e.currentTarget.scrollTop > 20);
    }, []);

    const handleScrollTop = useCallback(() => {
        scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        speak(t('list_type.detail.tts.moved_to_top'));
        setTimeout(() => {
            contentRef.current?.focus();
        }, 300);
    }, [speak, t]);

    return (
        <div className={styles.container}>
            <div
                className={clsx(styles.sidebar, {
                    [styles['sidebar--halfmode']]: screenMode === 'split',
                })}
            >
                <div className="flex flex-col">
                    <h2
                        className={clsx(styles['sidebar__title'], 'focus-border')}
                        tabIndex={1}
                        data-interactive
                        aria-label={title}
                        data-region-nav
                        data-region-order={1}
                    >
                        {title}
                    </h2>
                    <div className={clsx(styles['sidebar__date'], 'focus-border')} tabIndex={2} data-interactive aria-label={dateRangeTTS}>
                        <CalendarIcon width={18} height={18} color={!contrast ? '#444444' : '#ffffff'} />
                        <span>{dateRange}</span>
                    </div>
                </div>
                <button
                    type="button"
                    className={clsx(clsx(styles['sidebar__back']), 'focus-border', {
                        [styles['sidebar__back--halfmode']]: screenMode === 'split',
                    })}
                    onClick={onBack}
                    tabIndex={3}
                    data-interactive
                    aria-label={t('list_type.detail.back_to_list')}
                    data-role={t('common.data_role.button')}
                    data-region-nav
                    data-region-order={2}
                >
                    <BackIcon />
                    {t('list_type.detail.back_to_list')}
                </button>
            </div>

            <div
                className={clsx(styles.main, {
                    [styles['main--halfmode']]: screenMode === 'split',
                })}
            >
                <div
                    ref={scrollRef}
                    onScroll={handleScroll}
                    className={clsx(styles['main__body'], 'scrollbar-custom-gray-99938f scrollbar-w-6', {
                        [styles['main__body--fullsize']]: !detail.poiConnect || !detail.qrConnect,
                        [styles['halfmode']]: screenMode === 'split' && (!detail.poiConnect || !detail.qrConnect),
                        [styles['main__body--halfmode']]: screenMode === 'split' && (detail.poiConnect || detail.qrConnect),
                    })}
                >
                    <div
                        ref={contentRef}
                        className={clsx(styles['main__content'], 'focus-border')}
                        tabIndex={4}
                        data-interactive
                        aria-label={body}
                        data-region-nav
                        data-region-order={3}
                    >
                        {body}
                    </div>
                    {urlPreview && (
                        <div
                            className={clsx(styles['main__image'], 'focus-border')}
                            tabIndex={5}
                            data-interactive
                            aria-label={t('list_type.detail.tts.detail_image', { title })}
                            data-region-nav
                            data-region-order={4}
                        >
                            <img src={urlPreview} alt={title} loading="lazy" />
                        </div>
                    )}
                </div>
                {(detail.poiConnect || detail.qrConnect) && (
                    <div className={clsx(styles['main__actions'], { [styles['main__actions--halfmode']]: screenMode === 'split' })}>
                        <ListTypeDetailActions
                            onQrClick={onQrClick}
                            onDirectionClick={() => {
                                onDirectionClick?.(detail);
                            }}
                            tabIndexBase={7}
                            showDirection={detail.poiConnect}
                            showQr={detail.qrConnect}
                            qrRegionOrder={6}
                            directionRegionOrder={7}
                        />
                    </div>
                )}

                {showScrollTop && (
                    <button
                        type="button"
                        className={clsx(styles['scroll-top'], 'focus-border', {
                            [styles['scroll-top__halfmode']]: screenMode === 'split',
                        })}
                        onClick={handleScrollTop}
                        aria-label={t('list_type.detail.tts.go_to_top')}
                        tabIndex={6}
                        data-interactive
                        data-role={t('common.data_role.button')}
                        data-region-nav
                        data-region-order={5}
                    >
                        <ChevronUpIcon className={styles['scroll-top__icon']} />
                    </button>
                )}
            </div>
        </div>
    );
};

export default ListTypeDetailHorizontal;
